import { useParams, useSearchParams } from 'react-router-dom';
import type { FlowId } from '@/flows/types';
import { WORKFLOW_QUERY } from '@/utils/constants';
import {
  isAccessGroupCreationConversation,
  isBulkUserImportConversation,
  isDeviceSetupConversation,
  isUserCreationConversation,
} from '@/utils/conversationWorkflow';
import GuidedConversationPage from '@/pages/GuidedConversationPage';
import ConversationsPage from './ConversationsPage';

type ConversationRouteProps = {
  conversationId: string | undefined;
  workflowParam: string | null;
};

const GUIDED_FLOW_ROUTES: {
  flowId: FlowId;
  workflowQuery: string;
  isRegistered: (conversationId: string) => boolean;
}[] = [
  {
    flowId: 'device-setup',
    workflowQuery: WORKFLOW_QUERY.DEVICE_SETUP,
    isRegistered: isDeviceSetupConversation,
  },
  {
    flowId: 'bulk-user-import',
    workflowQuery: WORKFLOW_QUERY.BULK_USER_IMPORT,
    isRegistered: isBulkUserImportConversation,
  },
  {
    flowId: 'user-creation',
    workflowQuery: WORKFLOW_QUERY.USER_CREATION,
    isRegistered: isUserCreationConversation,
  },
  {
    flowId: 'access-group-creation',
    workflowQuery: WORKFLOW_QUERY.ACCESS_GROUP_CREATION,
    isRegistered: isAccessGroupCreationConversation,
  },
];

function matchesGuidedFlow(
  { conversationId, workflowParam }: ConversationRouteProps,
  route: (typeof GUIDED_FLOW_ROUTES)[number]
): boolean {
  if (!conversationId) return false;
  return workflowParam === route.workflowQuery || route.isRegistered(conversationId);
}

/** Picks guided flow page vs standard conversations layout. */
export default function ConversationRoute() {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const workflowParam = searchParams.get('workflow');

  const props = { conversationId, workflowParam };
  const match = GUIDED_FLOW_ROUTES.find((route) => matchesGuidedFlow(props, route));

  if (match && conversationId) {
    return <GuidedConversationPage flowId={match.flowId} />;
  }

  return <ConversationsPage />;
}
